/**
 * useUserSync Hook
 * 
 * Synchronizes user state from auth store with backend
 * Checks if user is verified and updates store accordingly
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@shared/lib/store/auth-store';
import { buildApiUrl } from '@shared/api/config';

const SYNC_INTERVAL = 30000; // 30 seconds

export function useUserSync() {
  const { user, isAuthenticated, updateUser } = useAuthStore();
  const lastSyncRef = useRef<number>(0);

  useEffect(() => {
    // Only sync if user is authenticated and not verified
    if (!isAuthenticated || !user || user.isVerified) {
      return;
    }

    const checkVerification = async () => {
      const now = Date.now();
      
      // Prevent too frequent checks
      if (now - lastSyncRef.current < SYNC_INTERVAL) {
        return;
      }

      lastSyncRef.current = now;

      try {
        // Call the /users/me endpoint to get fresh user data
        const response = await fetch(buildApiUrl('/users/me'), {
          credentials: 'include',
        });

        if (response.ok) {
          const userData = await response.json();
          
          // Update store if verification status changed
          if (userData && userData.isVerified !== user.isVerified) {
            updateUser({ isVerified: userData.isVerified });
          }
        }
      } catch (error) {
        console.error('[useUserSync] Failed to sync user data:', error);
      }
    };

    // Check immediately on mount
    checkVerification();

    // Set up interval for periodic checks
    const intervalId = setInterval(checkVerification, SYNC_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, user, updateUser]);
}
